import { FC, HTMLProps } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface Props extends Omit<HTMLProps<HTMLInputElement>, 'type' | 'value' | 'checked'> {
  value?: boolean
  label?: string
  variant?: 'primary' | 'latest'
}

const Switch: FC<Props> = ({ label: switchLabel, value, variant = 'primary', ...props }) => {
  const { t } = useTranslation()

  return (
    <Container variant={variant}>
      <input type="checkbox" checked={value} {...props} />
      {switchLabel && (
        <label {...(props?.id ? { htmlFor: props.id } : { style: { cursor: 'default' } })}>{t(switchLabel)}</label>
      )}
    </Container>
  )
}

const Container = styled.div<{ variant: Props['variant'] }>`
  --background: ${(p) => (p.variant === 'latest' ? p.theme.colors.gray4 : p.theme.colors.white)};
  --active: ${(p) => p.theme.colors.blue1};
  --focus: 1px ${(p) => p.theme.colors.blue2};
  --border: ${(p) => p.theme.colors.defaultBorder};
  --border-hover: ${(p) => p.theme.colors.blue1};
  --disabled: ${(p) => p.theme.colors.gray1};
  --disabled-checked: ${(p) => p.theme.colors.blue6};
  --inner: ${(p) => (p.variant === 'latest' ? p.theme.colors.white : p.theme.colors.gray6)};
  --inner-active: ${(p) => p.theme.colors.white};
  --inner-disabled: ${(p) => p.theme.colors.gray4};
  --switch-text-color: ${(p) => p.theme.colors.defaultText};
  --switch-text-color-disabled: ${(p) => p.theme.colors.gray5};

  input[type='checkbox'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 21px;
    width: 38px;
    border-radius: 11px;
    outline: none;
    display: inline-block;
    vertical-align: top;
    position: relative;
    margin: 0;
    cursor: pointer;
    border: 1px solid var(--bc, var(--border));
    background: var(--b, var(--background));
    transition: background 0.3s, border-color 0.3s, box-shadow 0.2s;

    &::after {
      content: '';
      display: block;
      position: absolute;
      transition: transform var(--d-t, 0.3s) var(--d-t-e, ease), opacity var(--d-o, 0.2s);
      left: 2px;
      top: 2px;
      border-radius: 50%;
      width: 15px;
      height: 15px;
      background: var(--ab, var(--inner));
      transform: translateX(var(--x, 0));
    }

    &:focus {
      box-shadow: 0 0 0 var(--focus);
    }

    &:checked {
      --b: var(--active);
      --bc: var(--active);
      --d-o: 0.3s;
      --d-t: 0.6s;
      --d-t-e: cubic-bezier(0.2, 0.85, 0.32, 1.2);
      --ab: var(--inner-active);
      --x: 17px;
    }

    &:disabled {
      --b: var(--disabled);

      cursor: not-allowed;
      opacity: 0.9;

      &:checked {
        --b: var(--disabled-checked);
        --bc: var(--border);
      }

      &:not(:checked)::after {
        opacity: 0.6;
      }
    }

    &:hover:not(:checked):not(:disabled) {
      --bc: var(--border-hover);
    }
  }

  label {
    font-size: 14px;
    line-height: 21px;
    display: inline-block;
    vertical-align: top;
    cursor: pointer;
    margin-left: 0.5rem;
    color: var(--switch-text-color);
  }

  input[type='checkbox']:disabled + label {
    cursor: not-allowed;
    color: var(--switch-text-color-disabled);
  }
`

export default Switch
